import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';
import { NotificationsService } from '../notifications/notifications.service';
import { authService } from '../auth.service';
import { SpinnerService } from '../loading-service';
import { RouteTrackerService } from '../request-source.service';

@Injectable()
export class SampleInterceptor implements HttpInterceptor {
  private allowedMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  private excludedUrls = [
    'auth/login', 
    'auth/token',
    'auth/close-session',   // <-- ADD THIS
    'users/saveFCM',
    'advance-search/last_used',
    'notifications',
    'reports/leads',
    'actions', // This will match all action endpoints
    'actions/stats',
    'actions/complete',
    'actions/snooze',
    'actions/view',
    'actions/clear-related',
    'actions/reactivate',
    'actions/priority',
    'actions/bulk-complete',
    'actions/overdue',
    'actions/by-entity',
    'translation',
    'advance-search/pinned',
    'advance-search/unpinned',
    'aws/signUrls',
    'analyze-completion',
    'general/search-history',
    'webrtc-credentials',
    'initiate',
    'call-dispositions',
    'users/theme/',
    'leads/validate-pipeline-bulk',
    'inbox/leads/mark-seen',
    'general/lead-step',
    'mark-as-started',
    'mark-as-not-started',
    'check-reopen-duplicates',
    'leads',
    'sticky-notes',
    'calendar/zoom-create-meeting',   // ← add
    'calendar/zoom-meeting',          // ← add (covers create/update/delete/get)
    'calendar/create-meeting',        // ← add (Google Meet)
    'calendar/zoom-auth-url',         // ← add (GET, but harmless to exclude)
    'my-rc-number/dismiss-prompt'
  ];

  constructor(
    private injector: Injector,
    private authService: authService,
    private spinnerService: SpinnerService,
    private routeTracker: RouteTrackerService, // <-- ADD
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const token = localStorage.getItem('accessToken');
    const notifier = this.injector.get(NotificationsService);

    const skipSpinner = request.url.includes('sticky-notes');
    if (!skipSpinner) {
      this.spinnerService.show();
    }

    const authEndpointsWithoutToken = [
      'auth/login',
      'auth/request-password-reset',
      'auth/reset-password',
      'auth/validate-token',
      'auth/token',
      'auth/close-session',
    ];

    const shouldSkipToken = authEndpointsWithoutToken.some(endpoint =>
      request.url.includes(endpoint)
    );

    // ===== TRACKING HEADERS (always attached) =====
    const trackingHeaders = {
      'X-Source-Component': this.routeTracker.currentComponent || 'Unknown',
      'X-Source-Route': this.routeTracker.currentRoute || 'Unknown',
    };

    let clonedRequest = request;
    if (token && !shouldSkipToken) {
      clonedRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...trackingHeaders, // <-- ADD
        },
      });
    } else if (!token) {
      clonedRequest = request.clone({
        setHeaders: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...trackingHeaders, // <-- ADD
        },
      });
    }

    return next.handle(clonedRequest).pipe(
      tap((event) =>
        this.handleSuccessResponse(event, clonedRequest, notifier),
      ),
      catchError((error) =>
        this.handleErrorResponse(error, clonedRequest, next, token, notifier),
      ),
      finalize(() => { if (!skipSpinner) this.spinnerService.hide(); }),
    );
  }

  private handleSuccessResponse(
    event: HttpEvent<any>,
    request: HttpRequest<unknown>,
    notifier: NotificationsService,
  ): void {
    if (
      event instanceof HttpResponse &&
      (event.status === 200 || event.status === 201) &&
      this.allowedMethods.includes(request.method)
    ) {
      if (!this.excludedUrls.some((url) => request.url.includes(url))) {
        if (!event.body?.requiresConfirmation) {
          // Only show message if it's a string, not an object
          const message = event.body?.message;
          const successMessage = (typeof message === 'string' && message) 
            ? message 
            : 'Operation successful';
          notifier.showNonErrorSnackBar(successMessage);
        }
      }
    }
  }

  private handleErrorResponse(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string | null,
    notifier: NotificationsService
  ): Observable<HttpEvent<unknown>> {
    // Handle 403 Forbidden
    if (error.status === 403 || error.statusText === 'Forbidden') {
      notifier.showNonErrorSnackBar('You do not have access to this action');
      return throwError(() => error);
    }
  
    // Handle 401 Unauthorized
    if (error.status === 401) {
      // Let login errors propagate
      if (request.url.includes('auth/login')) {
        return throwError(() => error);
      }
  
      // Any other 401 = token expired/invalid → logout
      this.authService.logout('expired');
      return throwError(() => error);
    }
  
    // Any other error
    return throwError(() => error);
  }
}